import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { getMetadataIssues } from "@/api/endpoints";
import { useApi } from "@/hooks/useApi";
import type { MetadataIssue } from "@/types/api";

type ItemTypeFilter = "all" | "movie" | "episode";
type MissingFilter =
  | "all"
  | "primary_artwork"
  | "episode_still"
  | "tmdb_id"
  | "imdb_id"
  | "tvdb_id"
  | "overview"
  | "year"
  | "title"
  | "naming";

const ISSUE_OPTIONS: Array<{ value: MissingFilter; label: string }> = [
  { value: "all", label: "All Issues" },
  { value: "primary_artwork", label: "Primary Art" },
  { value: "episode_still", label: "Episode Stills" },
  { value: "tmdb_id", label: "TMDb" },
  { value: "imdb_id", label: "IMDb" },
  { value: "tvdb_id", label: "TVDb" },
  { value: "overview", label: "Overview" },
  { value: "year", label: "Year" },
  { value: "title", label: "Title" },
  { value: "naming", label: "Naming" },
];

export function MetadataPage() {
  const [itemType, setItemType] = useState<ItemTypeFilter>("all");
  const [missing, setMissing] = useState<MissingFilter>("all");
  const [sort, setSort] = useState("title");

  const params = useMemo(() => ({
    limit: 100,
    sort,
    "filter[item_type]": itemType === "all" ? undefined : itemType,
    "filter[missing]": missing === "all" ? undefined : missing,
  }), [itemType, missing, sort]);

  const issues = useApi(() => getMetadataIssues(params), [itemType, missing, sort]);
  const summary = issues.data?.meta.summary;

  return (
    <div className="admin-page">
      <h1>Metadata Diagnostics</h1>
      <p className="settings-copy admin-page-copy">
        Fast metadata coverage from the server cache. Use this to find movies and episodes missing artwork,
        provider IDs, or other metadata before rescanning or rematching.
      </p>

      <section className="settings-section">
        <div className="settings-section-header">
          <h2>How To Use This</h2>
        </div>
        <p className="settings-copy">
          Start with a missing field like Primary Art or TMDb to narrow the list. Open an affected item when you want to
          rescan it, search for a better match, or verify whether the gap is expected.
        </p>
        <p className="settings-note">
          Coverage is based on the current library snapshot and metadata cache. It flags missing fields quickly, but it
          does not confirm whether a match is correct.
        </p>
      </section>

      <section className="settings-section">
        <div className="settings-section-header">
          <h2>Filters</h2>
        </div>
        <div className="diagnostics-toolbar">
          <div className="diagnostics-filter-group">
            <span className="diagnostics-filter-label">Type</span>
            <div className="diagnostics-chip-row">
              {(["all", "movie", "episode"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`diagnostics-chip ${itemType === value ? "diagnostics-chip--active" : ""}`}
                  onClick={() => setItemType(value)}
                >
                  {value === "all" ? "All" : value === "movie" ? "Movies" : "Episodes"}
                </button>
              ))}
            </div>
          </div>

          <div className="diagnostics-filter-group">
            <span className="diagnostics-filter-label">Missing</span>
            <div className="diagnostics-chip-row">
              {ISSUE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`diagnostics-chip ${missing === option.value ? "diagnostics-chip--active" : ""}`}
                  onClick={() => setMissing(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <label className="settings-field diagnostics-sort-field">
            <span>Sort</span>
            <select className="settings-select" value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="title">Title</option>
              <option value="-title">Title Desc</option>
              <option value="year">Year</option>
              <option value="-year">Year Desc</option>
              <option value="item_type">Type</option>
              <option value="series_title">Series</option>
            </select>
          </label>
        </div>
      </section>

      <section className="settings-section">
        <div className="settings-section-header">
          <h2>Coverage</h2>
        </div>
        {issues.loading && <p>Loading diagnostics...</p>}
        {issues.error && (
          <p className="settings-status settings-status--error">
            {issues.error instanceof ApiError ? issues.error.problem.detail : "Failed to load metadata diagnostics"}
          </p>
        )}
        {summary && (
          <>
            <div className="diagnostics-summary-grid">
              <SummaryCard label="Records" value={summary.inspected_records} />
              <SummaryCard label="Matches" value={summary.matched_records} />
              <SummaryCard label="Movies" value={summary.item_type_counts.movie ?? 0} />
              <SummaryCard label="Episodes" value={summary.item_type_counts.episode ?? 0} />
            </div>
            {summary.orphaned_records > 0 && (
              <p className="settings-note">
                {summary.orphaned_records.toLocaleString()} orphaned cache records are excluded from these live counts.
              </p>
            )}

            <div className="diagnostics-count-grid">
              <CountPill label="Primary Art" value={summary.missing_counts.primary_artwork ?? 0} />
              <CountPill label="Episode Stills" value={summary.missing_counts.episode_still ?? 0} />
              <CountPill label="TMDb" value={summary.missing_counts.tmdb_id ?? 0} />
              <CountPill label="IMDb" value={summary.missing_counts.imdb_id ?? 0} />
              <CountPill label="TVDb" value={summary.missing_counts.tvdb_id ?? 0} />
              <CountPill label="Overview" value={summary.missing_counts.overview ?? 0} />
              <CountPill label="Year" value={summary.missing_counts.year ?? 0} />
              <CountPill label="Title" value={summary.missing_counts.title ?? 0} />
              <CountPill label="Naming" value={summary.missing_counts.naming ?? 0} />
            </div>
          </>
        )}
      </section>

      <section className="settings-section">
        <div className="settings-section-header">
          <h2>Results</h2>
          {issues.data?.page.next_cursor && (
            <span className="badge badge--muted">Showing first {issues.data.page.limit}</span>
          )}
        </div>
        <p className="settings-note">
          Click through to the item page to rescan or rematch a specific movie or episode.
        </p>
        {issues.data?.page.next_cursor && (
          <p className="settings-note">
            Results are capped at the first {issues.data.page.limit}. Use filters to narrow the set before drilling in.
          </p>
        )}
        {!issues.loading && !issues.error && issues.data?.data.length === 0 && (
          <p>No matching metadata issues.</p>
        )}
        {issues.data && issues.data.data.length > 0 && (
          <table className="data-table diagnostics-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Type</th>
                <th>Missing</th>
                <th>IDs</th>
              </tr>
            </thead>
            <tbody>
              {issues.data.data.map((item) => (
                <MetadataIssueRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="diagnostics-summary-card">
      <div className="diagnostics-summary-value">{value.toLocaleString()}</div>
      <div className="diagnostics-summary-label">{label}</div>
    </div>
  );
}

function CountPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="diagnostics-count-pill">
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
    </div>
  );
}

function MetadataIssueRow({ item }: { item: MetadataIssue }) {
  const attrs = item.attributes;
  const ids = [
    attrs.tmdb_id ? "TMDb" : null,
    attrs.imdb_id ? "IMDb" : null,
    attrs.tvdb_id ? "TVDb" : null,
  ].filter(Boolean) as string[];

  return (
    <tr>
      <td>
        <div className="diagnostics-item-cell">
          <Link className="inline-link" to={`/items/${item.id}`}>
            {attrs.title}
          </Link>
          {attrs.series_title && (
            <div className="diagnostics-subtext">
              {attrs.series_title}
              {attrs.season_title ? ` • ${attrs.season_title}` : ""}
            </div>
          )}
          {attrs.source_path && (
            <div className="diagnostics-subtext diagnostics-source-path">
              {attrs.source_path}
            </div>
          )}
        </div>
      </td>
      <td>
        <span className="badge badge--muted">{attrs.item_type === "movie" ? "Movie" : "Episode"}</span>
      </td>
      <td>
        <div className="diagnostics-badge-list">
          {attrs.missing.map((value) => (
            <span key={value} className="badge">
              {formatMissingLabel(value)}
            </span>
          ))}
        </div>
      </td>
      <td>
        <div className="diagnostics-subtext">
          {ids.length > 0 ? ids.join(", ") : "No provider IDs"}
        </div>
      </td>
    </tr>
  );
}

function formatMissingLabel(value: string) {
  switch (value) {
    case "primary_artwork":
      return "Primary Art";
    case "episode_still":
      return "Episode Still";
    case "tmdb_id":
      return "TMDb";
    case "imdb_id":
      return "IMDb";
    case "tvdb_id":
      return "TVDb";
    case "overview":
      return "Overview";
    case "year":
      return "Year";
    case "title":
      return "Title";
    case "naming":
      return "Naming";
    case "poster":
      return "Poster";
    case "backdrop":
      return "Backdrop";
    default:
      return value;
  }
}
